/**
 * API Client for Cloudflare Migration Toolkit
 * Author: Codex
 * Company: HUB International, LLC
 */

import axios, { AxiosInstance, AxiosError } from "axios";
import type { LockedDomainOverridePayload } from "@/lib/lockedDomains";

// In development, use relative paths (proxied by Vite)
// In production, use the configured API URL or default to relative
const API_BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";

export interface ProjectStatus {
  project_name: string;
  exists: boolean;
  input_files?: string[];
  output_files?: string[];
  log_files?: string[];
  admin_folders?: Record<string, string[]>;
}

export interface ProjectSearchMatch {
  line: number;
  snippet: string;
}

export interface ProjectSearchResult {
  project_name: string;
  path: string;
  name: string;
  source: "local" | "r2";
  match_count: number;
  matches: ProjectSearchMatch[];
}

export interface ProjectSearchResponse {
  status: string;
  query: string;
  storage_backend: "local" | "r2";
  project_matches: string[];
  file_matches: ProjectSearchResult[];
  total_project_matches: number;
  total_file_matches: number;
}

export interface ProjectList {
  projects: ProjectStatus[];
}

export interface LockedDomainEntry {
  zone_id: string;
  zone_name: string;
  registry_key?: string;
  source: "file" | "workers_kv";
  lock_metadata: {
    reason?: string;
    stakeholder_contacts?: string[];
    locked_at?: string;
    locked_by?: string;
    active?: boolean;
  };
}

export interface LockedDomainsListResponse {
  status: string;
  sources: string[];
  warnings?: string[];
  count: number;
  active_count?: number;
  inactive_count?: number;
  total_entries?: number;
  locked_domains: LockedDomainEntry[];
}

export interface LockedDomainRegistryPayload {
  domain: string;
  zone_id?: string;
  reason?: string;
  stakeholder_contacts?: string[];
  locked_at?: string;
  locked_by?: string;
  active?: boolean;
  original_key?: string;
}

export type DnsBatchProvider = "cloudflare" | "csc";
export type DnsBatchAction = "ADD" | "UPDATE" | "DELETE";

export interface DnsBatchRowPayload {
  provider: DnsBatchProvider;
  action: DnsBatchAction;
  zone_name: string;
  record_id?: string;
  record_type?: string;
  name?: string;
  content?: string;
  ttl?: number;
  proxied?: boolean;
  priority?: number;
}

export interface DnsBatchResponse {
  status: "success" | "partial";
  total: number;
  success_count: number;
  failed_count: number;
  results: Array<{
    row: number;
    provider: DnsBatchProvider;
    action: DnsBatchAction;
    zone_name: string;
    record_id?: string;
    status: "success" | "submitted" | "failed";
    error?: string;
    record?: Record<string, unknown>;
    result?: Record<string, unknown>;
  }>;
}

export interface ProjectFile {
  name: string;
  path: string;
  type: "file" | "directory";
  size?: number;
  last_modified?: string;
}

export interface CloudflareZone {
  id: string;
  name: string;
  status: string;
  type: string;
  name_servers: string[];
  created_on: string;
  modified_on: string;
  plan: string | null;
  account?: {
    id: string;
    name: string;
  };
}

export interface ZoneListResponse {
  count: number;
  zones: CloudflareZone[];
  status: string;
}

export interface ProcurementJob {
  id: string;
  requested_by: string;
  status: "running" | "completed" | "failed" | "cancelled";
  progress: number;
  message: string;
  started_at: string;
  completed_at?: string | null;
  error?: string | null;
}

export interface ProcurementOverview {
  project_name: string;
  storage_backend: "local" | "r2";
  report_exists: boolean;
  json_exists: boolean;
  pdf_exists?: boolean;
  report_url?: string | null;
  json_url?: string | null;
  pdf_url?: string | null;
  summary: {
    generated_at?: string;
    account_id?: string;
    account_name?: string;
    total_zones?: number;
    zones_detailed?: number;
    subscription_records?: number;
    unique_products?: number;
    resource_counts?: Record<string, number>;
    http_requests?: number;
    http_bytes?: number;
    zone_plans?: Record<string, number>;
    feature_summary?: Record<string, number>;
  };
  latest_job?: ProcurementJob | null;
}

class ApiClient {
  private client: AxiosInstance;
  private accessHandshakeInFlight = false;
  // Cached user email from the last successful getMe() call.
  // Forwarded as X-Forwarded-User-Email so the Worker can re-attach it
  // as CF-Access-Authenticated-User-Email for /api/* Bypass routes.
  private cachedUserEmail: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      timeout: 300000, // 5m timeout to prevent hanging requests
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor for Zero Trust headers
    this.client.interceptors.request.use((config) => {
      // Track request start time for error handling
      (config as any)._requestStartTime = Date.now();

      // In development, we can set a mock user email
      if (import.meta.env.DEV && !config.headers["X-User-Email"]) {
        const devEmail = localStorage.getItem("dev_user_email") || "dev@hubinternational.com";
        config.headers["X-User-Email"] = devEmail;
      }

      // In production, forward the cached user email so the Cloudflare Worker can
      // re-attach it as CF-Access-Authenticated-User-Email after validating CF_AppSession.
      // This is needed because /api/* uses CF Access Bypass (no CF Access header injection),
      // but CF_AppSession still proves the user is authenticated.
      if (this.cachedUserEmail && !import.meta.env.DEV) {
        config.headers["X-Forwarded-User-Email"] = this.cachedUserEmail;
      }

      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Cloudflare Access returns a redirect to /cdn-cgi/access/login for expired/missing sessions.
        // XHR/fetch cannot follow that cross-origin redirect, which surfaces as CORS/network failures.
        console.log("[API] Error caught in interceptor:", {
          code: error.code,
          message: error.message,
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
        });

        // If the caller explicitly opted out of the redirect (e.g. bulk import loop),
        // just reject the error so it can be handled inline without navigating away.
        if ((error.config as any)?._skipAccessHandshake) {
          return Promise.reject(error);
        }

        const loginUrl = this.extractCloudflareAccessLoginUrl(error);
        if (loginUrl) {
          console.log("[API] Detected Access login redirect, redirecting to:", loginUrl);
          this.redirectToAccess(loginUrl);
          return new Promise(() => undefined);
        }

        // Fallback for CORS-blocked Access redirects where axios only reports ERR_NETWORK.
        if (this.shouldTriggerAccessHandshake(error)) {
          console.log("[API] Triggering Access handshake for CORS-blocked redirect");
          const returnPath = this.getCurrentRoutePath();
          this.redirectToAccess(this.buildCloudflareAccessHandshakeUrl(returnPath));
          return new Promise(() => undefined);
        }

        // Handle method not allowed (405) - don't trigger redirects, just reject
        if (error.response?.status === 405) {
          console.error("Method not allowed:", error.config?.method?.toUpperCase(), error.config?.url);
          // Don't trigger redirect for 405 - it's a client error, not an auth issue
          return Promise.reject(error);
        }

        // Handle permission errors (403) - show error modal
        if (error.response?.status === 403) {
          const errorData = error.response.data as { detail?: string; message?: string } | undefined;
          const errorMessage = errorData?.detail ||
            errorData?.message ||
            "Your API token lacks the necessary permissions to perform this operation. Please check your token permissions in the Config page.";
          // Import and use error context dynamically to avoid circular dependencies
          if (typeof window !== 'undefined') {
            // Dispatch a custom event that ErrorProvider can listen to
            window.dispatchEvent(new CustomEvent('api-permission-error', {
              detail: { message: errorMessage, title: 'Permission Denied' }
            }));
          }
          console.error("Permission denied:", errorMessage);
        }

        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to login or show error
          console.error("Unauthorized access");
        }
        return Promise.reject(error);
      }
    );
  }

  private extractCloudflareAccessLoginUrl(error: AxiosError): string | null {
    // Check responseURL (for actual redirects that were followed)
    const responseURL = (error.request as any)?.responseURL as string | undefined;
    if (responseURL && this.isCloudflareAccessLoginUrl(responseURL)) {
      console.log("[API] Found Access login URL in responseURL:", responseURL);
      return this.ensureRedirectUrl(responseURL);
    }

    // Check error message and stack for redirect URLs (CORS-blocked redirects show up here)
    // The browser console shows: "Access to XMLHttpRequest at 'https://hub-cf.cloudflareaccess.com/...'"
    const errorMessage = error.message || String(error) || "";
    const errorStack = error.stack || "";
    const errorString = String(error);
    const fullErrorText = [errorMessage, errorStack, errorString].join(" ");

    // Check if any part of the error contains Access login URL indicators
    if (this.isCloudflareAccessLoginUrl(fullErrorText)) {
      // Try multiple regex patterns to extract the URL
      // Pattern 1: Full URL in quotes: 'https://...' or "https://..."
      let urlMatch = fullErrorText.match(/['"](https?:\/\/[^'"]*cdn-cgi\/access\/login[^'"]*)['"]/);
      if (!urlMatch) {
        // Pattern 2: URL after "at '": at 'https://...'
        urlMatch = fullErrorText.match(/at\s+['"](https?:\/\/[^'"]*cdn-cgi\/access\/login[^'"]*)['"]/);
      }
      if (!urlMatch) {
        // Pattern 3: Any URL containing the Access login path (more lenient, handles truncated URLs)
        urlMatch = fullErrorText.match(/(https?:\/\/[^\s'")]*cdn-cgi\/access\/login[^\s'")]*)/);
      }
      if (!urlMatch) {
        // Pattern 4: Just check for the domain and path (handles very truncated URLs)
        urlMatch = fullErrorText.match(/(https?:\/\/[^/]*\/cdn-cgi\/access\/login[^\s'")]*)/);
      }

      if (urlMatch) {
        const extractedUrl = urlMatch[1];
        console.log("[API] Extracted Access login URL from error:", extractedUrl);
        // If URL is truncated, we can still use it to build a proper redirect
        if (extractedUrl.includes("redirect_url=")) {
          return this.ensureRedirectUrl(extractedUrl);
        } else {
          // URL is truncated, build redirect from current route
          const returnPath = this.getCurrentRoutePath();
          return this.buildCloudflareAccessHandshakeUrl(returnPath);
        }
      } else {
        // If we detect Access login in the error but can't extract URL, build one from the current route
        console.log("[API] Detected Access login redirect but couldn't extract URL, building redirect URL");
        const returnPath = this.getCurrentRoutePath();
        return this.buildCloudflareAccessHandshakeUrl(returnPath);
      }
    }

    // Check config URL (shouldn't happen, but just in case)
    const maybeConfigUrl = typeof error.config?.url === "string" ? error.config.url : "";
    if (maybeConfigUrl && this.isCloudflareAccessLoginUrl(maybeConfigUrl)) {
      console.log("[API] Found Access login URL in config URL:", maybeConfigUrl);
      return this.ensureRedirectUrl(maybeConfigUrl);
    }

    return null;
  }

  private isCloudflareAccessLoginUrl(url: string): boolean {
    return url.includes("/cdn-cgi/access/login");
  }

  private shouldTriggerAccessHandshake(error: AxiosError): boolean {
    // If we already have an HTTP response, this is not the CORS/network redirect case.
    if (error.response) return false;

    // Don't trigger redirect on timeout errors - these are expected for long-running operations
    // like webdetective scans. Timeout errors should be handled by the calling code.
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      return false;
    }

    // Don't trigger redirect for connection refused, connection reset, or other connection errors
    // These are likely backend errors, not Access redirects
    if (
      error.code === "ECONNREFUSED" ||
      error.code === "ECONNRESET" ||
      error.code === "ETIMEDOUT" ||
      error.message?.includes("Connection refused") ||
      error.message?.includes("Connection reset") ||
      error.message?.includes("Failed to fetch")
    ) {
      return false;
    }

    // Check if the error message contains a Cloudflare Access login URL
    // This happens when CORS blocks the redirect but the URL is in the error message
    const errorMessage = error.message || String(error) || "";
    const errorStack = error.stack || "";
    const fullErrorText = errorMessage + " " + errorStack;
    if (this.isCloudflareAccessLoginUrl(fullErrorText)) {
      console.log("[API] Detected Access login URL in error text, triggering handshake");
      return true; // This is definitely an Access redirect
    }

    // Don't trigger redirect for CORS errors that are NOT Access redirects
    // If it's a CORS error but contains the Access login URL, it IS an Access redirect
    if (
      error.message?.includes("CORS") &&
      !fullErrorText.includes("cdn-cgi/access/login") &&
      !fullErrorText.includes("cloudflareaccess.com")
    ) {
      return false;
    }

    // Don't trigger Cloudflare Access handshake for upload endpoints which might fail with typical network/CORS errors
    // when unhandled backend exceptions occur, to prevent infinite reloads.
    if (error.config?.url?.includes("/upload")) {
      return false;
    }

    const isNetworkFailure =
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      ((error.request as any)?.status ?? 0) === 0;

    if (!isNetworkFailure) return false;

    // Only trigger redirect for API requests, and only if we're reasonably sure it's an Access redirect
    // Check if the request was to a protected API endpoint
    const isApiReq = this.isApiRequest(error.config?.url, error.config?.baseURL);
    if (!isApiReq) return false;

    return true;
  }

  private isApiRequest(rawUrl?: string, rawBaseUrl?: string): boolean {
    const url = typeof rawUrl === "string" ? rawUrl : "";
    const baseUrl = typeof rawBaseUrl === "string" ? rawBaseUrl : API_BASE_URL;

    // If axios lost request config but user is already on protected frontend routes,
    // assume this network failure came from an API call that hit Access.
    if (!url && typeof window !== "undefined" && window.location.pathname.startsWith("/v1")) {
      return true;
    }

    const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
    const combinedPath = `${baseUrl}${normalizedUrl}`;
    return combinedPath.includes("/api/");
  }

  private getCurrentRoutePath(): string {
    if (typeof window === "undefined") return "/v1/dashboard";
    return this.normalizeProtectedRoutePath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    );
  }

  private redirectToAccess(targetUrl: string): void {
    if (typeof window === "undefined") return;
    if (this.accessHandshakeInFlight) return;

    // Safety check: never redirect to API endpoints
    if (targetUrl.includes("/api/")) {
      console.error("[API Client] Attempted to redirect to API endpoint:", targetUrl);
      return;
    }

    this.accessHandshakeInFlight = true;
    window.location.assign(targetUrl);
  }

  private ensureRedirectUrl(loginUrl: string): string {
    if (typeof window === "undefined") return loginUrl;
    if (loginUrl.includes("redirect_url=")) return loginUrl;

    const redirectTarget = `${window.location.pathname}${window.location.search}`;
    const separator = loginUrl.includes("?") ? "&" : "?";
    return `${loginUrl}${separator}redirect_url=${encodeURIComponent(redirectTarget)}`;
  }

  /**
   * Trigger a top-level Cloudflare Access login handshake for protected routes.
   * This avoids XHR CORS failures caused by Access redirects on /api/* and /v1/*.
   */
  buildCloudflareAccessHandshakeUrl(redirectPath: string = "/v1/dashboard"): string {
    const normalizedPath = this.normalizeProtectedRoutePath(redirectPath);

    // Do not hardcode /cdn-cgi/access/login endpoints. Access configuration varies by account and
    // may use the team domain for login. Redirecting to a protected route lets Access generate the
    // correct challenge URL automatically.
    if (typeof window === "undefined") {
      return normalizedPath;
    }

    return normalizedPath;
  }

  startCloudflareAccessHandshake(redirectPath: string = "/v1/dashboard") {
    if (typeof window === "undefined") return;
    const handshakeUrl = this.buildCloudflareAccessHandshakeUrl(redirectPath);
    window.location.assign(handshakeUrl);
  }

  private normalizeProtectedRoutePath(path: string): string {
    const fallback = "/v1/dashboard";
    if (!path) return fallback;

    const trimmed = path.trim();
    const prefixed = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

    // Access policies sometimes send users back to literal wildcard paths like /v1/*.
    // Normalize these to a concrete route so the SPA can render predictably.
    if (prefixed === "/v1" || prefixed === "/v1/" || prefixed === "/v1/*" || prefixed.includes("*")) {
      return fallback;
    }

    if (!prefixed.startsWith("/v1/")) {
      return fallback;
    }

    return prefixed;
  }

  // Health check (not under /api/v1 prefix, use direct axios call)
  async getHealth() {
    // Health endpoint is at root level, not under /api/v1
    const response = await axios.get("/health", {
      baseURL: "", // Override baseURL for this call
    });
    return response.data;
  }

  // Auth / Identity
  async getMe(): Promise<{ email: string | null; authenticated: boolean }> {
    const response = await this.client.get("/auth/me");
    const data = response.data as { email: string | null; authenticated: boolean };
    // Cache the email so the request interceptor can forward it on writes
    if (data.email) {
      this.cachedUserEmail = data.email;
    }
    return data;
  }

  // Projects
  async listProjects(): Promise<ProjectStatus[]> {
    const response = await this.client.get("/projects");
    return response.data;
  }

  async searchProjects(query: string): Promise<ProjectSearchResponse> {
    const response = await this.client.get("/projects/search/content", {
      params: { q: query },
    });
    return response.data;
  }

  async createProject(data: { project_name: string }) {
    const response = await this.client.post("/projects", data);
    return response.data;
  }

  async exportProjectDns(projectName: string) {
    const response = await this.client.post(`/projects/${projectName}/actions/export-dns`);
    return response.data;
  }

  async getProject(projectName: string): Promise<ProjectStatus> {
    const response = await this.client.get(`/projects/${projectName}`);
    return response.data;
  }

  async getProjectFileContent(projectName: string, path: string) {
    const response = await this.client.get(`/projects/${projectName}/files/view`, {
      params: { path }
    });
    return response.data;
  }

  async deleteProject(projectName: string) {
    const response = await this.client.delete(`/projects/${projectName}`);
    return response.data;
  }

  getProjectFileDownloadUrl(projectName: string, path: string) {
    // Construct absolute URL for direct browser navigation/download
    // Note: This bypasses axios interceptors but usually auth is cookie based or we need to append token if token-based.
    // Assuming cookie-based/zero-trust handled by browser.
    const baseUrl = this.client.defaults.baseURL || API_BASE_URL;
    let url = `${baseUrl}/projects/${projectName}/files/download?path=${encodeURIComponent(path)}`;
    if (this.cachedUserEmail) {
      url += `&cf_user_email=${encodeURIComponent(this.cachedUserEmail)}`;
    }
    return url;
  }

  // Locked domains (preflight)
  async getLockedDomainStatus(params: { zone_name?: string; zone_id?: string }) {
    const sp = new URLSearchParams();
    if (params.zone_name) sp.append("zone_name", params.zone_name);
    if (params.zone_id) sp.append("zone_id", params.zone_id);
    const q = sp.toString();
    const response = await this.client.get(`/locked-domains/status${q ? `?${q}` : ""}`);
    return response.data;
  }

  async listLockedDomains(): Promise<LockedDomainsListResponse> {
    const response = await this.client.get("/locked-domains");
    return response.data;
  }

  async saveLockedDomain(data: LockedDomainRegistryPayload) {
    const response = await this.client.put("/locked-domains", data);
    return response.data;
  }

  async deleteLockedDomain(registryKey: string) {
    const response = await this.client.delete(`/locked-domains/${encodeURIComponent(registryKey)}`);
    return response.data;
  }

  // Migration
  async getMigrationJob(jobId: string) {
    return (await this.client.get(`/migration/jobs/${encodeURIComponent(jobId)}`, { timeout: 15000 })).data;
  }

  async getLatestMigrationJob(project: string) {
    return (await this.client.get(`/migration/status/${encodeURIComponent(project)}`, { timeout: 15000 })).data;
  }

  private async runMigration(operation: string, data: unknown, onProgress?: (job: any) => void) {
    // The server deduplicates transport retries using this submission's identity.
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify([operation, data])));
    const key = "migration-submit:" + Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, "0")).join("");
    const requestId = sessionStorage.getItem(key) || crypto.randomUUID();
    sessionStorage.setItem(key, requestId);
    let job;
    try {
      const response = await this.client.post(`/migration/${operation}`, data, {
        timeout: 30000, headers: { "Idempotency-Key": requestId },
      });
      job = response.data;
      sessionStorage.removeItem(key);
    } catch (error) {
      // Unknown transport outcomes keep the identity so a retry cannot repeat mutations.
      if (axios.isAxiosError(error) && error.response) sessionStorage.removeItem(key);
      throw error;
    }
    onProgress?.(job);
    while (["queued", "running"].includes(job.status)) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      job = await this.getMigrationJob(job.id);
      onProgress?.(job);
    }
    if (job.result) return job.result;
    const error = new Error(job.error?.message || job.message || "Migration requires review");
    // Preserve the existing locked-domain confirmation workflow for asynchronous failures.
    Object.assign(error, { response: { status: job.http_status || 500, data: job.error || { message: error.message } } });
    throw error;
  }

  async migrationStep1(
    data: {
      project_name: string;
      csv_path?: string;
      csv_content?: string;
      domains?: string[];
      enable_worker_routes?: boolean;
      worker_script_name?: "mna-redirect-worker" | "mna-redirect-worker-staging";
    } & LockedDomainOverridePayload,
    onProgress?: (job: any) => void
  ) {
    return this.runMigration("step1", data, onProgress);
  }

  async migrationStep2(
    data: {
      project_name: string;
      csv_path?: string;
      csv_content?: string;
      domains?: string[];
    } & LockedDomainOverridePayload,
    onProgress?: (job: any) => void
  ) {
    return this.runMigration("step2", data, onProgress);
  }

  async migrationStep3(
    data: {
      project_name: string;
      csv_path?: string;
      csv_content?: string;
      domains?: string[];
      account_custom_ns_set_id?: number;
    } & LockedDomainOverridePayload,
    onProgress?: (job: any) => void
  ) {
    return this.runMigration("step3", data, onProgress);
  }

  async migrationAll(
    data: {
      project_name: string;
      csv_path?: string;
      csv_content?: string;
      domains?: string[];
      account_custom_ns_set_id?: number;
      enable_worker_routes?: boolean;
      worker_script_name?: "mna-redirect-worker" | "mna-redirect-worker-staging";
    } & LockedDomainOverridePayload,
    onProgress?: (job: any) => void
  ) {
    return this.runMigration("all", data, onProgress);
  }

  async migrationPreq(data: { project_name: string; csv_path?: string; domains?: string[]; refresh_inventory?: boolean },
    onProgress?: (job: any) => void) {
    return this.runMigration("preq", data, onProgress);
  }

  async migrationUpgrade(
    data: {
      project_name: string;
      csv_path?: string;
      csv_content?: string;
      domains?: string[];
    } & LockedDomainOverridePayload,
    onProgress?: (job: any) => void
  ) {
    return this.runMigration("upgrade", data, onProgress);
  }

  async migrationVerify(data: { project_name: string; csv_path?: string; domains?: string[] },
    onProgress?: (job: any) => void) {
    return this.runMigration("verify", data, onProgress);
  }

  async migrationKvUpload(
    data: {
      project_name: string;
      csv_path?: string;
      csv_content?: string;
      domains?: string[];
    } & LockedDomainOverridePayload,
    onProgress?: (job: any) => void
  ) {
    return this.runMigration("kv-upload", data, onProgress);
  }

  async getProcurementDashboard(): Promise<ProcurementOverview> {
    const response = await this.client.get("/procurement");
    return response.data;
  }

  async getProcurementReportJson(): Promise<Record<string, unknown>> {
    const response = await this.client.get("/procurement/report.json");
    return response.data;
  }

  async downloadProcurementPdf(): Promise<Blob> {
    const response = await this.client.get("/procurement/report.pdf", {
      responseType: "blob",
      timeout: 180000,
    });
    return response.data as Blob;
  }

  async refreshProcurementDashboard(): Promise<ProcurementJob> {
    const response = await this.client.post("/procurement/refresh");
    return response.data;
  }

  async getProcurementJob(jobId: string): Promise<ProcurementJob> {
    const response = await this.client.get(`/procurement/jobs/${jobId}`);
    return response.data;
  }

  async cancelProcurementJob(jobId: string): Promise<ProcurementJob> {
    const response = await this.client.post(`/procurement/jobs/${jobId}/cancel`);
    return response.data;
  }


  // Security
  async securityCheck(data: {
    project_name: string;
    csv_content?: string;
    zone_names?: string[];
    zone_ids?: string[];
    output_csv?: string;
    output_json?: string;
    capture_har?: boolean;
  }) {
    const response = await this.client.post("/security/check", data);
    return response.data;
  }

  async startSecurityCheck(data: { project_name: string; zone_names?: string[]; capture_har?: boolean }) {
    const response = await this.client.post("/security/check/jobs", data, { timeout: 30000 });
    return response.data;
  }

  async getSecurityCheckJob(id: string) {
    // Poll failures must remain recoverable; a transient network error is not proof of an expired Access session.
    const response = await this.client.get(`/security/check/jobs/${encodeURIComponent(id)}`, {
      timeout: 30000, _skipAccessHandshake: true,
    } as any);
    return response.data;
  }

  async downloadSecurityArtifact(projectName: string, artifact: "csv" | "pdf" | "json" | "har"): Promise<Blob> {
    const response = await this.client.get(
      `/security/reports/${encodeURIComponent(projectName)}/${artifact}`,
      { responseType: "blob", timeout: 180000 }
    );
    return response.data as Blob;
  }

  async webDetectiveScan(data: {
    url: string;
    project_name: string;
    use_browser?: boolean;
    capture_screenshots?: boolean;
    ignore_ssl_errors?: boolean;
    follow_redirects?: boolean;
    use_ai_agent?: boolean;
    enable_behavioral?: boolean;
    enable_browser_run?: boolean;
    browser_run_screenshot?: boolean;
    browser_run_record?: boolean;
    browser_run_record_policy?: string;
    browser_run_record_retention_days?: number;
    browser_run_webmcp?: boolean;
    browser_run_human_in_loop?: boolean;
    browser_run_reuse_session?: boolean;
    browser_run_force_escalation?: boolean;
  }) {
    // Web Detective scans can take a long time (browser automation, screenshots, etc.)
    // Use a longer timeout: 15 minutes (900000ms)
    const response = await this.client.post("/security/detect", data, {
      timeout: 900000,
    });
    return response.data;
  }

  async downloadWebDetectivePdf(data: {
    project_name: string;
    report?: Record<string, unknown>;
    report_path?: string;
    filename?: string;
  }): Promise<Blob> {
    const response = await this.client.post("/security/detect/pdf", data, {
      responseType: "blob",
      timeout: 180000,
    });
    return response.data as Blob;
  }

  async webDetectiveScanStream(
    data: {
      url: string;
      project_name: string;
      use_browser?: boolean;
      capture_screenshots?: boolean;
      ignore_ssl_errors?: boolean;
      follow_redirects?: boolean;
      use_ai_agent?: boolean;
      enable_behavioral?: boolean;
      enable_browser_run?: boolean;
      browser_run_screenshot?: boolean;
      browser_run_record?: boolean;
      browser_run_record_policy?: string;
      browser_run_record_retention_days?: number;
      browser_run_webmcp?: boolean;
      browser_run_human_in_loop?: boolean;
      browser_run_reuse_session?: boolean;
      browser_run_force_escalation?: boolean;
    },
    onMessage: (message: string) => void,
    onResult: (result: any) => void,
    onError: (error: any) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Create a fetch request instead of using axios because XHR/Axios doesn't handle SSE reading incrementally easily
      const handleStream = async () => {
        try {
          const authHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
          };

          // We can try forwarding cached emails if in dev or whatever, but standard credentials usually pass
          if (this.cachedUserEmail && !import.meta.env.DEV) {
            authHeaders["X-Forwarded-User-Email"] = this.cachedUserEmail;
          }

          // Inject profile into the body as fallback since custom headers aren't always preserved or trusted in some proxy setups
          const requestData = {
            ...data,
            profile: this.cachedUserEmail || localStorage.getItem("dev_user_email") || "dev@hubinternational.com"
          };

          const response = await fetch(`${API_BASE_URL}/security/detect/stream`, {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify(requestData),
          });

          if (!response.ok) {
            const errText = await response.text();
            throw new Error(`HTTP error ${response.status}: ${errText}`);
          }

          if (!response.body) {
            throw new Error("ReadableStream not supported in this browser.");
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { value, done } = await reader.read();
            buffer += decoder.decode(value, { stream: !done });
            const chunks = buffer.split(/\r?\n\r?\n/);
            buffer = chunks.pop() || "";
            if (done && buffer.trim()) chunks.push(buffer);

            for (const chunk of chunks) {
              const dataStr = chunk.split(/\r?\n/)
                .filter(line => line.startsWith('data:'))
                .map(line => line.slice(5).trimStart()).join('\n');
              if (!dataStr) continue;
              let payload;
              try {
                payload = JSON.parse(dataStr);
              } catch {
                throw new Error("Invalid scan response received from the server.");
              }
              if (payload.error || payload.result?.error || payload.result?.status === 'error') {
                throw new Error(payload.error || payload.result?.error || "Scan failed.");
              }
              if (payload.message) onMessage(payload.message);
              if (payload.result) onResult(payload.result);
            }
            if (done) break;
          }
          resolve();
        } catch (e) {
          onError(e);
          reject(e);
        }
      };

      handleStream();
    });
  }

  // DNS
  async listDNSRecords(zoneName: string) {
    const response = await this.client.get(`/dns/zones/${zoneName}/records`);
    return response.data;
  }

  async createDNSRecord(
    zoneName: string,
    data: {
      record_type: string;
      name: string;
      content: string;
      ttl?: number;
      proxied?: boolean;
      priority?: number;
    } & LockedDomainOverridePayload
  ) {
    const response = await this.client.post(`/dns/zones/${zoneName}/records`, data);
    return response.data;
  }

  async updateDNSRecord(
    zoneName: string,
    recordId: string,
    data: {
      record_type?: string;
      name?: string;
      content?: string;
      ttl?: number;
      proxied?: boolean;
      priority?: number;
    } & LockedDomainOverridePayload
  ) {
    const response = await this.client.put(`/dns/zones/${zoneName}/records/${recordId}`, data);
    return response.data;
  }

  async deleteDNSRecord(zoneName: string, recordId: string, opts?: LockedDomainOverridePayload) {
    const params = new URLSearchParams();
    if (opts?.override_locked_domain) params.append("override_locked_domain", "true");
    if (opts?.override_confirmation) params.append("override_confirmation", opts.override_confirmation);
    const q = params.toString();
    await this.client.delete(
      `/dns/zones/${encodeURIComponent(zoneName)}/records/${encodeURIComponent(recordId)}${q ? `?${q}` : ""}`
    );
  }

  async applyDNSBatch(data: { rows: DnsBatchRowPayload[] } & LockedDomainOverridePayload): Promise<DnsBatchResponse> {
    const response = await this.client.post("/dns/batch", data);
    return response.data;
  }

  // CSC DNS
  async listCSCDNSRecords(zoneName: string) {
    const response = await this.client.get(`/csc-dns/zones/${zoneName}/records`);
    return response.data;
  }

  async submitCSCZoneEdits(zoneName: string, edits: any[], opts?: LockedDomainOverridePayload) {
    const params = new URLSearchParams();
    if (opts?.override_locked_domain) params.append("override_locked_domain", "true");
    if (opts?.override_confirmation) params.append("override_confirmation", opts.override_confirmation);
    const q = params.toString();
    const response = await this.client.post(
      `/csc-dns/zones/${encodeURIComponent(zoneName)}/edits${q ? `?${q}` : ""}`,
      edits
    );
    return response.data;
  }

  async getCSCZoneEditStatus(editId: string) {
    const response = await this.client.get(`/csc-dns/edits/${editId}`);
    return response.data;
  }

  // Workers KV
  async generateKVCSV(data: { csv_path?: string; domains?: string[]; output_csv?: string; project_name?: string }) {
    const response = await this.client.post("/workers/kv/generate", data);
    return response.data;
  }

  async uploadKVCSV(
    data: { csv_path?: string; csv_content?: string; environment?: string } & LockedDomainOverridePayload
  ) {
    const response = await this.client.post("/workers/kv/upload", data);
    return response.data;
  }

  async getKVPairs(data?: { output_csv?: string; environment?: string; project_name?: string; namespace_id?: string; search?: string }) {
    const params = new URLSearchParams();
    if (data?.output_csv) params.append("output_csv", data.output_csv);
    if (data?.environment) params.append("environment", data.environment);
    if (data?.project_name) params.append("project_name", data.project_name);
    if (data?.namespace_id) params.append("namespace_id", data.namespace_id);
    if (data?.search) params.append("search", data.search);
    const queryString = params.toString();
    const url = `/workers/kv/download${queryString ? `?${queryString}` : ""}`;
    const response = await this.client.get(url, { responseType: "blob" });
    return response.data;
  }

  async listKVNamespaces() {
    const response = await this.client.get("/workers/kv/namespaces");
    return response.data;
  }

  async listKVPairs(
    environment: string = "prod",
    options?: { prefix?: string; search?: string; limit?: number; offset?: number; namespaceId?: string }
  ) {
    const params = new URLSearchParams();
    params.append("environment", environment);
    if (options?.namespaceId) params.append("namespace_id", options.namespaceId);
    if (options?.prefix) params.append("prefix", options.prefix);
    if (options?.search) params.append("search", options.search);
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());
    const response = await this.client.get(`/workers/kv/list?${params.toString()}`);
    return response.data;
  }

  async addKVPair(key: string, value: string, environment: string = "prod", namespaceId?: string) {
    const params = new URLSearchParams();
    params.append("key", key);
    params.append("value", value);
    params.append("environment", environment);
    if (namespaceId) params.append("namespace_id", namespaceId);

    const response = await this.client.post(`/workers/kv/add?${params.toString()}`);
    return response.data;
  }

  async deleteKVPair(key: string, environment: string = "prod", namespaceId?: string) {
    const params = new URLSearchParams();
    params.append("environment", environment);
    if (namespaceId) params.append("namespace_id", namespaceId);

    const response = await this.client.delete(`/workers/kv/delete/${encodeURIComponent(key)}?${params.toString()}`);
    return response.data;
  }


  // Certificates
  async uploadCertificate(
    data: {
      zone_id: string;
      pfx_path: string;
      password?: string;
      cert_id?: string;
    } & LockedDomainOverridePayload
  ) {
    const response = await this.client.post("/certificates/upload", data);
    return response.data;
  }

  async issueMtlsCert(
    data: {
      zone_id: string;
      vendor: string;
      validity_days?: number;
      project_name?: string;
    } & LockedDomainOverridePayload
  ) {
    const response = await this.client.post("/certificates/mtls/issue", data);
    return response.data;
  }

  async listMtlsCerts(zoneId: string) {
    const response = await this.client.post("/certificates/mtls/list", { zone_id: zoneId });
    return response.data;
  }

  async revokeMtlsCert(zoneId: string, certId: string, opts?: LockedDomainOverridePayload) {
    const response = await this.client.post("/certificates/mtls/revoke", {
      zone_id: zoneId,
      cert_id: certId,
      ...opts,
    });
    return response.data;
  }

  async exportMtlsCert(vendor: string, projectName?: string) {
    const response = await this.client.post("/certificates/mtls/export", {
      vendor,
      project_name: projectName,
    });
    return response.data;
  }

  // Export
  async exportCscDomains(data: { output_csv?: string; project_name?: string; domain_filter?: string }) {
    // Override timeout to 15 minutes
    const response = await this.client.post("/export/csc-domains", data, {
      responseType: 'blob',
      timeout: 900000
    });
    return response;
  }

  async exportCloudflareZones(data: { output_csv: string; project_name?: string; domain_filter?: string }) {
    // Override timeout to 15 minutes for large exports
    const response = await this.client.post("/export/cloudflare-zones", data, {
      responseType: 'blob',
      timeout: 900000
    });
    return response;
  }

  async buildIdMap() {
    const response = await this.client.post("/export/id-map", {});
    return response.data;
  }

  // Account
  async listZones() {
    const response = await this.client.get<ZoneListResponse>("/domains/");
    return response.data;
  }

  // Documentation
  async getOpenApiSpec() {
    const response = await this.client.get("/openapi.json");
    return response.data;
  }

  async getConfig() {
    const response = await this.client.get("/account/config");
    return response.data;
  }

  async getCredentialTemplate() {
    const response = await this.client.get("/account/config/template");
    return response.data;
  }

  async updateConfig(data: { settings: Record<string, any>; profile?: string }) {
    const response = await this.client.put("/account/config", data);
    return response.data;
  }

  async updateSecret(key: string, value: string, profile?: string) {
    const response = await this.client.put(`/account/config/secret/${key}`, { value, profile });
    return response.data;
  }

  async deleteSecret(key: string, profile?: string) {
    const response = await this.client.delete(`/account/config/secret/${key}`, {
      params: { profile },
    });
    return response.data;
  }

  async decryptSecret(encryptedValue: string, key: string): Promise<{ decrypted: string }> {
    const response = await this.client.post("/account/config/secret/decrypt", {
      value: encryptedValue,
      key,
    });
    return response.data;
  }

  async updateDecryptionKey(key: string): Promise<{ message: string; status: string }> {
    const response = await this.client.put("/account/config/decryption-key", { key });
    return response.data;
  }

  async importCredentials() {
    const response = await this.client.post("/account/config/import-local");
    return response.data;
  }

  // Helper function for reading file content, moved outside uploadCredentials
  // to be accessible after the function's body was replaced.
  private async readText(f: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error(`Failed to read ${f.name}`));
      reader.readAsText(f);
    });
  }

  async uploadCredentials(credentialsFile: File, _keyFile?: File) {
    const credsText = await this.readText(credentialsFile);
    let credentials: Record<string, string>;
    try {
      credentials = JSON.parse(credsText);
    } catch {
      const err: any = new Error("Invalid JSON format in credentials file.");
      err.response = { status: 400, data: { detail: "Invalid JSON format in credentials file." } };
      throw err;
    }

    let keyText: string | undefined = undefined;
    if (_keyFile) {
      keyText = await this.readText(_keyFile);
    }

    // Call the bulk import JSON endpoint
    const response = await this.client.post(
      `/account/config/import-json`,
      {
        credentials,
        key: keyText,
        replace: true // Completely replace old credentials
      }
    );

    return response.data;
  }

  async migrateAccount(
    data: {
      csv_path?: string;
      domains?: string[];
      source_account_id: string;
      source_api_token: string;
      project_name: string;
    } & LockedDomainOverridePayload
  ) {
    const response = await this.client.post("/account/migrate", data);
    return response.data;
  }

  async validateAccounts(data: {
    source_account_id: string;
    source_api_token: string;
  }) {
    const response = await this.client.post("/account/validate", data);
    return response.data;
  }

  // Ray ID lookup
  async lookupRayId(data: {
    ray_id: string;
    zone_id?: string;
    zone_name?: string;
    hours?: number;
    limit?: number;
  }) {
    const response = await this.client.post("/ray-id/lookup", data);
    return response.data;
  }

  // Panic
  async initiatePanic(data: any) {
    const response = await this.client.post("/panic/initiate", data);
    return response.data;
  }

  async restorePanic(data: any) {
    const response = await this.client.post("/panic/restore", data);
    return response.data;
  }
}

export const api = new ApiClient();
export default api;
